import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../../store/loginReducer";

const getStorageKey = (role) => `${role}_loginState`;

const useSessionTimeout = (timeoutMinutes = 60) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const role = location.pathname.includes("/student") ? "student" : "admin";
  const { isLoggedIn, user } = useSelector((state) => state.login[role]);
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const handleLogout = useCallback(() => {
    dispatch(logout(role));
    const redirectPath = role === "student" ? "/student" : "/admin";
    try {
      navigate(redirectPath, { replace: true });
      setTimeout(() => {
        window.location.href = redirectPath;
      }, 100);
    } catch (error) {
      console.error("Session timeout: Navigate failed, using window.location", error);
      window.location.href = redirectPath;
    }
  }, [dispatch, navigate, role]);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!isLoggedIn || !user) {
      return;
    }

    const timeoutMs = timeoutMinutes * 60 * 1000;
    lastActivityRef.current = Date.now();

    timeoutRef.current = setTimeout(() => {
      const loginState = localStorage.getItem(getStorageKey(role));
      if (loginState) {
        try {
          const parsed = JSON.parse(loginState);
          if (parsed?.isLoggedIn && parsed?.user) {
            handleLogout();
          } else {
            console.log("Session timeout: User already logged out");
          }
        } catch (error) {
          console.error("Session timeout: Error checking login state:", error);
        }
      } else {
        console.log("Session timeout: No login state found");
      }
    }, timeoutMs);
  }, [isLoggedIn, user, timeoutMinutes, handleLogout, role]);

  useEffect(() => {
    if (!isLoggedIn || !user) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    resetTimer();

    const activityEvents = [
      "mousedown",
      "click",
      "keydown",
      "keypress",
      "touchstart",
      "scroll",
    ];

    const handleActivity = (event) => {
      if (event?.isTrusted === false) {
        return;
      }
      
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      
      if (timeSinceLastActivity < 1000) {
        return;
      }
      
      lastActivityRef.current = now;
      resetTimer();
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetTimer();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLoggedIn, user, resetTimer]);

  useEffect(() => {
    if (isLoggedIn && user) {
      resetTimer();
    }
  }, [isLoggedIn, user, resetTimer]);
};

export default useSessionTimeout;

